import { Extension } from '@tiptap/core';
import { Suggestion } from '@tiptap/suggestion';
import type { SuggestionOptions } from '@tiptap/suggestion';
import type { Plugin } from '@tiptap/pm/state';

import { FIELD_NODE_NAME } from './fieldNode';
import { FIELD_TRIGGER, findFieldSuggestion } from './fieldSuggestionMatch';
import { needsTrailingSpace, tokenReplacementEnd } from './tokenRange';

export const FIELD_SUGGESTION_NAME = 'fieldSuggestion';

/** One entry in the picker. `filter` is offered so a money or date field arrives formatted. */
export interface FieldSuggestionItem {
	name: string;
	label: string;
	filter?: string;
	/** Which object the value comes from, e.g. "Investor profile". Groups the palette, and is
	 *  the only thing telling two entries called "First name" apart. */
	group?: string;
	/** What it resolves to for the recipient being previewed. Shown in place of the token: a
	 *  value an author recognises says more about a data point than its path does. */
	sample?: string;
	/** The path is pinned to the FIRST item of a list, so every other item is dropped. */
	firstOfList?: boolean;
}

export interface FieldSuggestionOptions {
	/** Everything the caller is prepared to offer; filtering by query happens here. */
	items: (query: string) => FieldSuggestionItem[];
	/** Supplied by the React layer. Absent in tests, where only the matching is exercised. */
	render?: SuggestionOptions<FieldSuggestionItem>['render'];
}

/**
 * Case-insensitive match on the label, its group, or the underlying name.
 *
 * The group is matched because it is no longer shown as part of the label: "Security name" lives
 * under "Holdings & securities", and an author who types `holding` expects to find it.
 */
export const filterFieldItems = (
	items: FieldSuggestionItem[],
	query: string
): FieldSuggestionItem[] => {
	const needle = query.trim().toLowerCase();
	if (needle === '') {
		return items;
	}
	return items.filter((item) =>
		[item.label, item.group ?? '', item.name].some((text) =>
			text.toLowerCase().includes(needle)
		)
	);
};

/**
 * Typing `{{` opens a searchable list of data points; choosing one inserts a field NODE.
 *
 * The inserted node replaces the typed `{{ …` including the trigger, so the document never
 * holds a half-written token — which is the state every silent-blank failure starts from.
 */
export const FieldSuggestion = Extension.create<FieldSuggestionOptions>({
	name: FIELD_SUGGESTION_NAME,

	addOptions() {
		return { items: () => [] };
	},

	addProseMirrorPlugins(): Plugin[] {
		return [
			Suggestion<FieldSuggestionItem>({
				editor: this.editor,
				char: FIELD_TRIGGER,
				findSuggestionMatch: findFieldSuggestion,
				items: ({ query }) => this.options.items(query),
				...(this.options.render === undefined ? {} : { render: this.options.render }),
				command: ({ editor, range, props }) => {
					// The WHOLE token goes, not just the query: with the caret inside an
					// already-closed `{{ … }}` the closing braces sit after the caret, and
					// replacing the query alone leaves them stranded beside the new field.
					const to = tokenReplacementEnd(editor.state, range.to);
					editor
						.chain()
						.focus()
						.insertContentAt({ from: range.from, to }, [
							{
								type: FIELD_NODE_NAME,
								attrs: {
									name: props.name,
									filter: props.filter ?? null,
								},
							},
							// A trailing space, so the caret lands in text rather than against
							// the atom and the next keystroke is not swallowed by it.
							...(needsTrailingSpace(editor.state, to)
								? [{ type: 'text', text: ' ' }]
								: []),
						])
						.run();
				},
			}),
		];
	},
});
